import { useState, useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import useDebounce from '@/hooks/useDebounce'
import {
    getCustomerOptions,
    getCustomerContains,
} from '@/modules/Customer/services'
import { Label } from '@/components/ui/label'
import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList,
} from '@/components/ui/command'
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from '@/components/ui/popover'
import { Button } from '@/components/ui/button'
import { Check, ChevronsUpDown, Loader2 } from 'lucide-react'
import { cn } from '@/lib/utils'

export interface CustomerOption {
    label: string
    value: number
}

interface CustomerPickerProps {
    value?: number
    onChange: (customerId: number | undefined, customerName?: string) => void
    placeholder?: string
    label?: string
    disabled?: boolean
}

const CustomerPicker = ({
    value,
    onChange,
    placeholder = 'Select customer...',
    label,
    disabled = false,
}: CustomerPickerProps) => {
    const [open, setOpen] = useState(false)
    const [inputValue, setInputValue] = useState('')
    const [selectedLabel, setSelectedLabel] = useState<string>('')

    // Debounce the search input
    const debouncedSearch = useDebounce(inputValue, 300)

    // Fetch customers based on debounced search query
    const { data: options = [], isLoading } = useQuery({
        queryKey: ['warehouse-customer-options', debouncedSearch],
        queryFn: () =>
            getCustomerOptions({ page: 1, page_size: 20, q: debouncedSearch }),
        enabled: open,
        staleTime: 30000,
    })

    // Fetch selected customer details when value changes
    const { data: selectedOptions = [] } = useQuery({
        queryKey: ['warehouse-customer-selected', value],
        queryFn: () => getCustomerContains({ customer_ids: [value!] }),
        enabled: !!value && !selectedLabel,
        staleTime: 60000,
    })

    // Update selected label when we get the customer details
    useEffect(() => {
        if (selectedOptions.length > 0 && value) {
            const selected = selectedOptions.find((opt) => opt.value === value)
            if (selected) {
                setSelectedLabel(selected.label)
            }
        }
    }, [selectedOptions, value])

    const handleSelect = (option: CustomerOption) => {
        onChange(option.value, option.label)
        setSelectedLabel(option.label)
        setOpen(false)
    }

    const handleClear = () => {
        onChange(undefined)
        setSelectedLabel('')
        setInputValue('')
    }

    return (
        <div className="space-y-2">
            {label && <Label>{label}</Label>}
            <Popover open={open} onOpenChange={setOpen}>
                <PopoverTrigger asChild>
                    <Button
                        variant="outline"
                        role="combobox"
                        aria-expanded={open}
                        disabled={disabled}
                        className="w-full justify-between font-normal"
                    >
                        <span className="truncate">
                            {value && selectedLabel
                                ? selectedLabel
                                : placeholder}
                        </span>
                        <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[400px] p-0" align="start">
                    <Command shouldFilter={false}>
                        <CommandInput
                            placeholder="Search customer..."
                            onValueChange={setInputValue}
                        />
                        <CommandList>
                            {isLoading ? (
                                <div className="flex items-center justify-center py-6">
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                    <span className="ml-2 text-sm text-muted-foreground">
                                        Loading...
                                    </span>
                                </div>
                            ) : options.length === 0 ? (
                                <CommandEmpty>No customer found.</CommandEmpty>
                            ) : (
                                <CommandGroup>
                                    {options.map((option) => (
                                        <CommandItem
                                            key={option.value}
                                            value={String(option.value)}
                                            onSelect={() =>
                                                handleSelect(option)
                                            }
                                        >
                                            <Check
                                                className={cn(
                                                    'mr-2 h-4 w-4',
                                                    value === option.value
                                                        ? 'opacity-100'
                                                        : 'opacity-0'
                                                )}
                                            />
                                            <span className="truncate">
                                                {option.label}
                                            </span>
                                        </CommandItem>
                                    ))}
                                </CommandGroup>
                            )}
                        </CommandList>
                    </Command>
                    {value && (
                        <div className="border-t p-2">
                            <Button
                                variant="ghost"
                                size="sm"
                                className="w-full text-muted-foreground"
                                onClick={handleClear}
                            >
                                Clear selection
                            </Button>
                        </div>
                    )}
                </PopoverContent>
            </Popover>
        </div>
    )
}

export default CustomerPicker
